import React from "react";
import { ShieldCheckIcon, UserIcon, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { UserRowModel } from "./SettingsUsersTable";
import { usePromoteUser } from "@/hooks/usePromoteUser";
import { customToast } from "@/utils/customToast";
import * as Sentry from "@sentry/react";
import { useT, type MessageKey } from "@/i18n";

type RoleType = "admin" | "normal";

type RoleOption = {
  value: RoleType;
  label: MessageKey;
  icon: LucideIcon;
};

type EditRoleDialogProps = {
  show: boolean;
  onCancel: () => void;
  user: UserRowModel;
  onRoleChangeSuccess?: () => void;
};

export function EditRoleDialog({ show, onCancel, user, onRoleChangeSuccess }: EditRoleDialogProps) {
  const initialRole = user.is_root ? "admin" : "normal";
  const [selectedRole, setSelectedRole] = React.useState<RoleType>(initialRole);
  const promoteUser = usePromoteUser();
  const { t } = useT();

  const isRoleChanged = selectedRole !== initialRole;

  const roleOptions: RoleOption[] = [
    {
      value: "admin",
      label: "users.admin",
      icon: ShieldCheckIcon,
    },
    {
      value: "normal",
      label: "users.normalUser",
      icon: UserIcon,
    },
  ];

  const handleSubmit = async () => {
    try {
      // NOTE: The user cannot be demoted to a normal user
      if (selectedRole === "admin" && !user.is_root) {
        await promoteUser.mutateAsync({ userId: user.id });
        customToast(t("users.roleChanged"), "success", "bottom");
      } else {
        console.error("Invalid role change operation", {
          selectedRole,
          isUserRoot: user.is_root,
          userId: user.id,
        });

        Sentry.captureEvent({
          message: "Invalid role change operation attempted",
          level: "error",
          extra: {
            selectedRole,
            isUserRoot: user.is_root,
            userId: user.id,
            initialRole,
          },
        });

        customToast(t("users.roleChangeNotPermitted"), "error", "bottom");
        return;
      }

      if (onRoleChangeSuccess) {
        onRoleChangeSuccess();
      } else {
        onCancel();
      }
    } catch (error) {
      console.error("Failed to update user privileges:", error);
      customToast(t("users.roleChangeFailed"), "error", "bottom");
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader className="pe-12">
          <DialogTitle dir="auto" className="truncate leading-normal">
            {user.name || user.email || "—"}
          </DialogTitle>
        </DialogHeader>
        <FieldSet className="px-4 py-4">
          <FieldLegend className="text-muted-foreground">{t("users.roles")}</FieldLegend>
          {roleOptions.map(({ value, label, icon: Icon }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted has-checked:border-primary has-checked:bg-accent has-checked:text-accent-foreground"
            >
              <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
              <span className="flex-1">{t(label)}</span>
              <input
                type="radio"
                name="role"
                value={value}
                checked={selectedRole === value}
                onChange={() => setSelectedRole(value)}
                className="size-4 shrink-0 accent-primary"
              />
            </label>
          ))}
        </FieldSet>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!isRoleChanged || promoteUser.isPending}>
            {promoteUser.isPending ? t("common.loadingEllipsis") : t("common.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
